import { useState, useRef, useCallback } from 'react';
import type { ZoomLevel, AggregatedDataPoint } from '../types';
import { CONFIG } from '../constants';

export const useZoomControls = (
    aggregatedData: AggregatedDataPoint[],
    panningState: any,
    visibleData: AggregatedDataPoint[],
    isExpanded: boolean,
    onZoomChange?: (newLevel: ZoomLevel, targetDate?: Date, isButtonClick?: boolean, cursorX?: number) => void
) => {
    const [zoomLevel, setZoomLevel] = useState<ZoomLevel>('month');
    const lastZoomTime = useRef<number>(0);

    const calculateTargetIndex = useCallback((cursorX?: number, useRecentDate = false): number => {
        if (useRecentDate) {
            // Use the correct window size based on expanded state for button clicks
            const windowSizes = isExpanded ? CONFIG.EXPANDED_WINDOW_SIZES : CONFIG.WINDOW_SIZES;
            const windowSize = windowSizes[zoomLevel];
            const currentOffset = panningState.tempOffset ?? panningState.panOffset;
            const startIndex = Math.max(0, Math.floor(currentOffset));
            const endIndex = Math.min(aggregatedData.length, startIndex + windowSize);
            return Math.max(0, endIndex - 1);
        }

        if (cursorX !== undefined) {
            const chartSelector = isExpanded ? '[data-chart-container-modal]' : '[data-chart-container]';
            const chartElement = document.querySelector(chartSelector);

            if (chartElement && visibleData.length > 0) {
                const rect = chartElement.getBoundingClientRect();
                const relativeX = cursorX - rect.left;
                const chartWidth = rect.width;
                const leftMargin = CONFIG.CHART_MARGINS.left;
                const rightMargin = CONFIG.CHART_MARGINS.right;
                const plotWidth = chartWidth - leftMargin - rightMargin;

                if (relativeX > leftMargin && relativeX < chartWidth - rightMargin) {
                    const plotRelativeX = relativeX - leftMargin;
                    const barWidth = plotWidth / visibleData.length;
                    const dataPointIndex = Math.floor(plotRelativeX / barWidth);
                    const clampedIndex = Math.max(0, Math.min(visibleData.length - 1, dataPointIndex));

                    // Use the correct window size based on expanded state
                    const windowSizes = isExpanded ? CONFIG.EXPANDED_WINDOW_SIZES : CONFIG.WINDOW_SIZES;
                    const windowSize = windowSizes[zoomLevel];
                    const currentOffset = panningState.tempOffset ?? panningState.panOffset;
                    const startIndex = Math.max(0, Math.floor(currentOffset));
                    return startIndex + clampedIndex;
                }
            }
        }

        // Default fallback - use correct window size based on expanded state
        const windowSizes = isExpanded ? CONFIG.EXPANDED_WINDOW_SIZES : CONFIG.WINDOW_SIZES;
        const windowSize = windowSizes[zoomLevel];
        const currentOffset = panningState.tempOffset ?? panningState.panOffset;
        const startIndex = Math.max(0, Math.floor(currentOffset));
        return startIndex + Math.floor(windowSize / 2);
    }, [aggregatedData, panningState, visibleData, zoomLevel, isExpanded]);

    const handleZoom = useCallback((direction: 'in' | 'out', cursorX?: number, useRecentDate = false) => {
        const now = Date.now();
        if (now - lastZoomTime.current < CONFIG.ZOOM_COOLDOWN) return;

        const zoomMaps = {
            in: { year: 'month', month: 'day', day: null } as const,
            out: { year: null, month: 'year', day: 'month' } as const,
        };

        const nextLevel = zoomMaps[direction][zoomLevel];
        if (nextLevel) {
            const targetIndex = calculateTargetIndex(cursorX, useRecentDate);
            const targetDate = aggregatedData[targetIndex]?.period;

            setZoomLevel(nextLevel);
            lastZoomTime.current = now;

            if (onZoomChange && targetDate) {
                onZoomChange(nextLevel, targetDate, useRecentDate, cursorX);
            }
        }
    }, [zoomLevel, calculateTargetIndex, aggregatedData, onZoomChange]);

    return {
        zoomLevel,
        handleZoomIn: (cursorX?: number, useRecentDate = false) => handleZoom('in', cursorX, useRecentDate),
        handleZoomOut: (cursorX?: number, useRecentDate = false) => handleZoom('out', cursorX, useRecentDate),
        canZoomIn: zoomLevel !== 'day',
        canZoomOut: zoomLevel !== 'year',
    };
};
